import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { compensationService } from '@/services/compensation/compensationService';
import { compensationKeys } from './queryKeys';
import { supabase } from '@/integrations/supabase/client';
import type { CompensationRuleQuery, PaidLeaveEntry } from '@/services/compensation/types';

export const useCompensationRules = (query: CompensationRuleQuery) => {
  return useQuery({
    queryKey: compensationKeys.rule(query.profileId, query.ruleType),
    queryFn: () => compensationService.getEmployeeCompensationRules(query),
    enabled: !!query.profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error.message.includes('JWT') || error.message.includes('Unauthorized')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Fetches paid leave data via edge function (service role) to bypass RLS,
 * then passes it into the compensation calculation engine.
 */
async function fetchPaidLeaveMap(
  profileIds: string[],
  startDate: string,
  endDate: string
): Promise<Map<string, PaidLeaveEntry[]>> {
  const paidLeaveMap = new Map<string, PaidLeaveEntry[]>();

  const { data: leaveResponse, error: leaveError } = await supabase.functions.invoke(
    'payroll-leave-data',
    {
      body: { profileIds, startDate, endDate },
    }
  );

  if (leaveError) {
    throw new Error(`Failed to fetch leave data for preview: ${leaveError.message}`);
  }

  const leaveDataRecord = leaveResponse?.data || {};
  for (const [profileId, empLeave] of Object.entries(leaveDataRecord)) {
    const leaveData = empLeave as { approved_leave: Array<{ leave_type: string; start_date: string; end_date: string; computed_hours: number }> };
    const entries: PaidLeaveEntry[] = [];
    for (const leave of leaveData.approved_leave) {
      if (leave.computed_hours > 0) {
        entries.push({
          leaveType: leave.leave_type,
          startDate: leave.start_date,
          endDate: leave.end_date,
          hours: leave.computed_hours,
        });
      }
    }
    if (entries.length > 0) {
      paidLeaveMap.set(profileId, entries);
    }
  }

  return paidLeaveMap;
}

export const usePayrollPreview = (profileIds: string[], startDate: Date, endDate: Date, enabled = true) => {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  return useQuery({
    queryKey: compensationKeys.preview(profileIds, startDateStr, endDateStr),
    queryFn: async () => {
      // Fetch leave data via edge function (bypasses RLS) in parallel-safe manner
      const paidLeaveMap = await fetchPaidLeaveMap(profileIds, startDateStr, endDateStr);

      return compensationService.generatePayrollPreview({
        profileIds,
        startDate,
        endDate,
        paidLeaveMap,
      });
    },
    enabled: enabled && profileIds.length > 0,
    staleTime: 30 * 1000,
    gcTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error.message.includes('JWT') || error.message.includes('Unauthorized')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};